import styled from '@emotion/styled';
import colors from '../../theme/colors';
import { FONT_FAMILIES } from '../../theme/material/constants';
import Button from '../../atoms/buttons/Button';
import { getButtonTheme, getTextColor } from '../../style/index';
import { Typography } from '@material-ui/core';
import { isDarkMode } from '../../utils/helpers';
import borders from '../../theme/borders';
import breakpoints from '../../theme/material/breakpoints';

export const LineView = styled('div')({
  position: 'absolute',
  top: 0,
  height: 6,
  backgroundColor: 'white',
  borderRadius: 3,
  zIndex: 3,
});

export const DotsWrapper = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 8px',
  borderRadius: '8px',
  background: '#262626',
  gap: 6,
});

export const DotsAnimatedWrapper = styled('div')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  left: 0,
  transition: 'transform 0.6s ease-out',
});

export const RenderDotsWrapper = styled('div')<{ hideDots: boolean }>(
  {
    height: 6,
    alignItems: 'center',
    position: 'relative',
    transition: 'opacity 0.5s, transform 0.5s',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
  },
  ({ hideDots }) => ({
    display: hideDots ? 'none' : 'flex',
  })
);
export const PublicDotIcon = styled('div')<{ activeDot: boolean }>(
  {
    width: 6,
    height: 6,
    borderRadius: '50%',
  },
  ({ activeDot }) => ({
    backgroundColor: activeDot ? 'white' : colors.gray['500'],
  })
);

export const SmallDotIcon = styled('div')<{ isPublic: boolean }>(
  {
    width: 4,
    height: 4,
    borderRadius: '50%',
  },
  ({ isPublic }) => ({
    backgroundColor: isPublic ? colors.gray['500'] : '#D06369',
  })
);

export const PrivateDotIcon = styled('div')<{ activeDot: boolean }>(
  {
    borderRadius: '50%',
  },
  ({ activeDot }) => ({
    backgroundColor: activeDot ? '#E21D4A' : '#D06369',
    border: activeDot ? '1px solid white' : 'none',
    width: activeDot ? 4 : 6,
    height: activeDot ? 4 : 6,
  })
);

export const loadMoreButtonStyle = {
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: '16px',
  height: 'auto !important',
  marginTop: 10,
  minWidth: '70px',
  padding: '8px 18px',
  borderRadius: '4px !important',
  borderWidth: 1,
  letterSpacing: '-0.26px',
};

export const DashlineSeparator = styled('div')(() => {
  const theme = isDarkMode() ? 'dark' : 'light';

  return {
    height: 1,
    borderTop: `1px dashed ${borders[theme].primary}`,
    margin: '20px 0',
  };
});

export const HCaptchaContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 20,
});

export const CloseBtn = styled('button')({
  appearance: 'none',
  border: 'none',
  background: 'none',
  width: '30px',
  height: '30px',
  position: 'absolute',
  top: -10,
  right: -10,
  '&:hover': {
    cursor: 'pointer',
  },
});

export const closeButton = {
  fontSize: 24,
  color: '#8F8F8F',
};

export const buttonStyle = {
  fontFamily: FONT_FAMILIES.primary,
  margin: '0 auto',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '16px',
  padding: '12px 22px',
  minWidth: '192px',
  borderRadius: '4px !important',
  '&:disabled': {
    background: `${isDarkMode() ? colors.gray['700'] : colors.gray['200']} !important`,
    border: 'none !important',
    color: `${getButtonTheme().buttonText2} !important`,
  },
  marginBottom: 15,
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
export const ContinueButton = styled(Button)(({ isLogin = false }) => ({
  background: !isLogin ? (isDarkMode() ? colors.black : colors.gray['100']) : colors.gray['100'],
  border: `1px solid ${!isLogin ? (isDarkMode() ? colors.gray['1500'] : colors.gray['1200']) : colors.gray['1200']}`,
  borderRadius: '20px',
  fontSize: '14px !important',
  fontWeight: 400,
  fontFamily: FONT_FAMILIES.primary,
  letterSpacing: '-0.28px',
  width: '100%',
  height: 40,
  color: !isLogin ? (isDarkMode() ? colors.dark.black.secondary : colors.light.gray.light) : colors.light.gray.light,
}));

export const contactSupportLinkStyle = {
  fontWeight: 400,
  color: colors.gray['1100'],
  lineHeight: '17px',
};

export const HeadlineContainer = styled('div')(() => ({
  textAlign: 'center',
  ...getTextColor().BRAND,
}));

export const Headline = styled('h1')({
  margin: 0,
  display: 'inline-block',
  color: colors.light.blue.primary,
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.48px',
});

export const StepText = styled(Typography)({
  display: 'inline-block',
  fontSize: 16,
  fontWeight: 400,
  color: colors.light.gray.light,
  letterSpacing: '-0.3px',
});

export const emailCustomStyle = {
  fontSize: 15,
  marginTop: 20,
  fontWeight: 700,
};

export const editEmailStyle = {
  fontSize: 15,
  color: colors.light.blue.primary,
  fontWeight: 700,
  textDecoration: 'underline',
};

export const bottomLinkStyle = {
  fontSize: 14,
  color: colors.gray['1100'],
  lineHeight: '17px',
};

export const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

export const mobileContentStyle = {
  borderRadius: 10,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
export const CancelButton = styled(Button)(() => ({
  fontSize: '14px',
  color: '#2b8fd7',
  fontWeight: 600,
  borderRadius: '6px',
  border: '1px solid #2b8fd7',
  display: 'flex',
  alignContent: 'center',
  alignItems: 'center',
  padding: '2px 16px',
  cursor: 'pointer',
  height: '32px',
  lineHeight: '17px',
  marginTop: '16px',
  '&:hover': {
    color: '#2b8fd7',
  },
  [breakpoints.down(639.05)]: {
    border: 'none',
    padding: '5px',

    '& svg': {
      width: '32px',
      height: '32px',
    },
  },
}));
